import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

export type StatusType = 'active' | 'paused' | 'inactive' | 'syncing' | 'warning' | 'error';
export type IndicatorSize = 'small' | 'medium' | 'large';

const STATUS_COLORS: Record<StatusType, string> = {
    active: 'var(--success-color, #10b981)',
    paused: 'var(--warning-color, #f59e0b)',
    inactive: 'var(--secondary-color, #64748b)',
    syncing: 'var(--primary-color, #2563eb)',
    warning: 'var(--warning-color, #f59e0b)',
    error: 'var(--error-color, #ef4444)',
};

const PULSE_BY_DEFAULT: Record<StatusType, boolean> = {
    active: true,
    paused: false,
    inactive: false,
    syncing: true,
    warning: false,
    error: false,
};

/**
 * A status indicator component that displays a colored dot with optional pulse animation.
 *
 * @fires status-change - Fired when the status property changes (detail: { status: string })
 */
@customElement('status-indicator')
export class StatusIndicator extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
            }

            .dot {
                border-radius: 50%;
                flex-shrink: 0;
            }

            /* Size variants */
            :host([size="small"]) .dot {
                width: 12px;
                height: 12px;
            }

            :host([size="medium"]) .dot {
                width: 36px;
                height: 36px;
            }

            :host([size="large"]) .dot {
                width: 48px;
                height: 48px;
            }

            /* Pulse animation */
            .dot.pulse {
                animation: pulse 2s infinite;
            }

            @keyframes pulse {
                0%, 100% {
                    opacity: 1;
                    transform: scale(1);
                }
                50% {
                    opacity: 0.7;
                    transform: scale(1.2);
                }
            }

            .label {
                font-size: 0.875rem;
                color: var(--text-secondary, #cbd5e1);
            }

            :host([size="large"]) .label {
                font-size: 1rem;
            }

            :host([size="small"]) .label {
                font-size: 0.75rem;
            }
        `,
    ];

    @property({ type: String, reflect: true })
    status: StatusType = 'inactive';

    @property({ type: String })
    label = '';

    @property({ type: String, reflect: true })
    size: IndicatorSize = 'medium';

    @property({ type: Boolean })
    pulse: boolean | undefined = undefined;

    private _shouldPulse(): boolean {
        if (this.pulse !== undefined) {
            return this.pulse;
        }
        return PULSE_BY_DEFAULT[this.status] ?? false;
    }

    updated(changedProperties: Map<string, unknown>): void {
        if (changedProperties.has('status')) {
            this.dispatchEvent(
                new CustomEvent('status-change', {
                    bubbles: true,
                    composed: true,
                    detail: { status: this.status },
                })
            );
        }
    }

    render() {
        const color = STATUS_COLORS[this.status] ?? STATUS_COLORS.inactive;
        const shouldPulse = this._shouldPulse();

        return html`
            <div
                class="dot ${shouldPulse ? 'pulse' : ''}"
                style="background-color: ${color}"
                role="status"
                aria-label=${this.label || this.status}
            ></div>
            ${this.label ? html`<span class="label">${this.label}</span>` : ''}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'status-indicator': StatusIndicator;
    }
}
